import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { 
  ServiceTaskTemplate, 
  ServiceTaskTemplateFormData, 
  ServiceTaskTemplatesFilter,
  ServiceTaskTemplateChecklist,
  ServiceTaskTemplateChecklistFormData,
  ServiceTaskInstance,
  ServiceTaskInstanceFormData,
  ServiceTaskInstancesFilter,
  ServiceTaskInstanceChecklist
} from '../types';

interface ServiceTaskTemplateStore {
  // State
  templates: ServiceTaskTemplate[];
  templateInstances: ServiceTaskInstance[];
  selectedTemplate: ServiceTaskTemplate | null;
  selectedInstance: ServiceTaskInstance | null;
  loading: boolean;
  error: string | null;
  
  // Filters
  templatesFilter: ServiceTaskTemplatesFilter;
  instancesFilter: ServiceTaskInstancesFilter;
  
  // Actions - Templates
  fetchTemplates: () => Promise<void>;
  fetchTemplatesByService: (serviceId: string) => Promise<void>;
  fetchTemplateById: (id: string) => Promise<void>;
  createTemplate: (data: Partial<ServiceTaskTemplateFormData>) => Promise<ServiceTaskTemplate | null>;
  updateTemplate: (id: string, data: Partial<ServiceTaskTemplateFormData>) => Promise<ServiceTaskTemplate | null>;
  deleteTemplate: (id: string) => Promise<boolean>;
  duplicateTemplate: (id: string, newServiceId?: string) => Promise<ServiceTaskTemplate | null>;
  reorderTemplates: (serviceId: string, templateIds: string[]) => Promise<boolean>;

  // Actions - Template Sections
  createSection: (serviceId: string, sectionName: string) => Promise<ServiceTaskTemplate | null>;
  updateSection: (sectionId: string, name: string) => Promise<boolean>;
  deleteSection: (sectionId: string) => Promise<boolean>;
  moveTemplateToSection: (templateId: string, sectionId: string | null) => Promise<boolean>;

  // Actions - Import/Export JSON
  exportTemplatesAsJson: (serviceId: string) => Promise<string>;
  importTemplatesFromJson: (
    serviceId: string,
    json: string,
    mode: 'replace' | 'append',
    onProgress?: (current: number, total: number) => void
  ) => Promise<{ success: boolean; imported: number; error?: string }>;

  // Actions - Template Checklist
  fetchTemplateChecklist: (templateId: string) => Promise<ServiceTaskTemplateChecklist[]>;
  createChecklistItem: (templateId: string, data: Partial<ServiceTaskTemplateChecklistFormData>) => Promise<ServiceTaskTemplateChecklist | null>;
  updateChecklistItem: (id: string, data: Partial<ServiceTaskTemplateChecklistFormData>) => Promise<ServiceTaskTemplateChecklist | null>;
  deleteChecklistItem: (id: string) => Promise<boolean>;
  reorderChecklistItems: (templateId: string, itemIds: string[]) => Promise<boolean>;
  
  // Actions - Task Instances
  fetchTaskInstances: () => Promise<void>;
  fetchTaskInstancesByDeal: (dealId: string) => Promise<void>;
  fetchTaskInstanceById: (id: string) => Promise<void>;
  createTaskInstance: (data: Partial<ServiceTaskInstanceFormData>) => Promise<ServiceTaskInstance | null>;
  updateTaskInstance: (id: string, data: Partial<ServiceTaskInstanceFormData>) => Promise<ServiceTaskInstance | null>;
  deleteTaskInstance: (id: string) => Promise<boolean>;
  
  // Actions - Instance Checklist
  updateInstanceChecklistItem: (id: string, isCompleted: boolean, timeSpentMinutes?: number) => Promise<boolean>;
  
  // Automation
  createTasksFromTemplates: (dealId: string, dealServiceId: string, serviceId: string, triggerEvent?: string) => Promise<number>;
  
  // Utilities
  setTemplatesFilter: (filter: ServiceTaskTemplatesFilter) => void;
  setInstancesFilter: (filter: ServiceTaskInstancesFilter) => void;
  clearFilters: () => void;
  setSelectedTemplate: (template: ServiceTaskTemplate | null) => void;
  setSelectedInstance: (instance: ServiceTaskInstance | null) => void;
  clearError: () => void;
}

export const useServiceTaskTemplateStore = create<ServiceTaskTemplateStore>((set, get) => ({
  // Initial state
  templates: [],
  templateInstances: [],
  selectedTemplate: null,
  selectedInstance: null,
  loading: false,
  error: null,
  templatesFilter: {},
  instancesFilter: {},

  // Actions - Templates
  fetchTemplates: async () => {
    try {
      set({ loading: true, error: null });

      const { templatesFilter } = get();
      let query = supabase
        .from('service_task_templates')
        .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
        .order('created_at', { ascending: true });

      // Apply filters
      if (templatesFilter.search) {
        query = query.or(`name.ilike.%${templatesFilter.search}%,description.ilike.%${templatesFilter.search}%`);
      }
      
      if (templatesFilter.priority) {
        query = query.eq('priority', templatesFilter.priority);
      }
      
      if (templatesFilter.assign_to_type) {
        query = query.eq('assign_to_type', templatesFilter.assign_to_type);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ templates: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching service task templates:', error);
      
      // Check if it's the RLS configuration error
      if (error instanceof Error && error.message.includes('app.current_organization_id')) {
        console.warn('RLS configuration issue detected. Service task templates feature is temporarily unavailable.');
        set({ 
          templates: [], 
          loading: false,
          error: 'I template dei task sono temporaneamente non disponibili a causa di un problema di configurazione del database.'
        });
        return;
      }
      
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei template',
        loading: false 
      });
    }
  },

  fetchTemplatesByService: async (serviceId: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_task_templates')
        .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
        .eq('service_id', serviceId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      set({ templates: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching service task templates:', error);
      
      // Check if it's the RLS configuration error
      if (error instanceof Error && error.message.includes('app.current_organization_id')) {
        console.warn('RLS configuration issue detected. Service task templates feature is temporarily unavailable.');
        set({ 
          templates: [], 
          loading: false,
          error: 'I template dei task sono temporaneamente non disponibili a causa di un problema di configurazione del database.'
        });
        return;
      }
      
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei template del servizio',
        loading: false 
      });
    }
  },

  fetchTemplateById: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_task_templates')
        .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      set({ selectedTemplate: data, loading: false });
    } catch (error) {
      console.error('Error fetching service task template:', error);
      
      // Check if it's the RLS configuration error
      if (error instanceof Error && error.message.includes('app.current_organization_id')) {
        console.warn('RLS configuration issue detected. Service task templates feature is temporarily unavailable.');
        set({ 
          selectedTemplate: null, 
          loading: false,
          error: 'I template dei task sono temporaneamente non disponibili a causa di un problema di configurazione del database.'
        });
        return;
      }
      
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento del template',
        loading: false 
      });
    }
  },

  createTemplate: async (data: Partial<ServiceTaskTemplateFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // Clean up UUID fields - convert empty strings to null
      const cleanData = { ...data };
      
      // UUID fields that should be null if empty (only fields that exist in schema)
      const uuidFields = ['service_id', 'assigned_user_id', 'assigned_partner_id'];
      uuidFields.forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === undefined) {
          cleanData[field] = null;
        }
      });

      // Map assign_to_type values to valid database values
      const mapAssignToType = (value: string | undefined): string => {
        const mapping: Record<string, string> = {
          'deal_owner': 'deal_referent',
          'user': 'specific_user',
          'partner': 'service_referent',
          'role': 'auto',
          'deal_referent': 'deal_referent',
          'specific_user': 'specific_user',
          'service_referent': 'service_referent',
          'auto': 'auto'
        };
        return mapping[value || 'deal_owner'] || 'deal_referent';
      };

      // Only include fields that exist in the database schema
      const templateData = {
        organization_id: userProfile.organization_id,
        created_by: userData.user.id,
        updated_by: userData.user.id,
        // Map form fields to database fields that exist
        service_id: cleanData.service_id || null, // Now we can include service_id
        name: cleanData.title || cleanData.name || '',
        description: cleanData.description || '',
        trigger_event: cleanData.trigger_event || 'service_added',
        trigger_days_offset: cleanData.days_after_start || cleanData.trigger_days_offset || 0,
        assign_to_type: mapAssignToType(cleanData.assign_to_type),
        assigned_user_id: cleanData.assigned_user_id || null,
        assigned_partner_id: cleanData.assigned_partner_id || null,
        priority: cleanData.priority || 'medium',
        estimated_duration_minutes: cleanData.estimated_hours ? cleanData.estimated_hours * 60 : (cleanData.estimated_duration_minutes || null),
        task_location: cleanData.task_location || 'deal',
        is_active: cleanData.is_active !== undefined ? cleanData.is_active : true,
        section: cleanData.section || null // ID della sezione di appartenenza
      };

      const { data: newTemplate, error } = await supabase
        .from('service_task_templates')
        .insert(templateData)
        .select('*')
        .single();

      if (error) throw error;

      set(state => ({
        templates: [...state.templates, newTemplate],
        loading: false
      }));

      // Try to refresh the list, but don't fail if it errors
      try {
        await get().fetchTemplates();
      } catch (refreshError) {
        console.warn('Could not refresh templates list after creation:', refreshError);
        // Continue anyway - the template was created successfully
      }

      return newTemplate;
    } catch (error) {
      console.error('Error creating service task template:', error);
      
      // Check if it's the RLS configuration error
      if (error instanceof Error && error.message.includes('app.current_organization_id')) {
        console.warn('RLS configuration issue detected. Service task templates feature is temporarily unavailable.');
        set({ 
          loading: false,
          error: 'I template dei task sono temporaneamente non disponibili a causa di un problema di configurazione del database.'
        });
        return null;
      }
      
      set({ 
        error: friendlyDbError(error) || 'Errore nella creazione del template',
        loading: false 
      });
      return null;
    }
  },

  updateTemplate: async (id: string, data: Partial<ServiceTaskTemplateFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Clean up UUID fields - convert empty strings to null
      const cleanData = { ...data };
      
      // UUID fields that should be null if empty (only fields that exist in schema)
      const uuidFields = ['service_id', 'assigned_user_id', 'assigned_partner_id'];
      uuidFields.forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === undefined) {
          cleanData[field] = null;
        }
      });

      // Map assign_to_type values to valid database values
      const mapAssignToType = (value: string | undefined): string => {
        const mapping: Record<string, string> = {
          'deal_owner': 'deal_referent',
          'user': 'specific_user',
          'partner': 'service_referent',
          'role': 'auto',
          'deal_referent': 'deal_referent',
          'specific_user': 'specific_user',
          'service_referent': 'service_referent',
          'auto': 'auto'
        };
        return mapping[value || 'deal_owner'] || 'deal_referent';
      };

      // Only include fields that exist in the database schema
      const updateData = {
        updated_by: userData.user.id,
        // Map form fields to database fields that exist
        ...(cleanData.service_id !== undefined && { service_id: cleanData.service_id }),
        ...(cleanData.title && { name: cleanData.title }),
        ...(cleanData.name && { name: cleanData.name }),
        ...(cleanData.description !== undefined && { description: cleanData.description }),
        ...(cleanData.trigger_event && { trigger_event: cleanData.trigger_event }),
        ...(cleanData.days_after_start !== undefined && { trigger_days_offset: cleanData.days_after_start }),
        ...(cleanData.trigger_days_offset !== undefined && { trigger_days_offset: cleanData.trigger_days_offset }),
        ...(cleanData.assign_to_type && { assign_to_type: mapAssignToType(cleanData.assign_to_type) }),
        ...(cleanData.assigned_user_id !== undefined && { assigned_user_id: cleanData.assigned_user_id }),
        ...(cleanData.assigned_partner_id !== undefined && { assigned_partner_id: cleanData.assigned_partner_id }),
        ...(cleanData.priority && { priority: cleanData.priority }),
        ...(cleanData.estimated_hours !== undefined && { estimated_duration_minutes: cleanData.estimated_hours * 60 }),
        ...(cleanData.estimated_duration_minutes !== undefined && { estimated_duration_minutes: cleanData.estimated_duration_minutes }),
        ...(cleanData.task_location && { task_location: cleanData.task_location }),
        ...(cleanData.is_active !== undefined && { is_active: cleanData.is_active }),
        ...(cleanData.section !== undefined && { section: cleanData.section || null })
      };

      const { data: updatedTemplate, error } = await supabase
        .from('service_task_templates')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
        .single();

      if (error) throw error;

      set(state => ({
        templates: state.templates.map(template => 
          template.id === id ? updatedTemplate : template
        ),
        selectedTemplate: state.selectedTemplate?.id === id ? updatedTemplate : state.selectedTemplate,
        loading: false
      }));

      return updatedTemplate;
    } catch (error) {
      console.error('Error updating service task template:', error);
      
      // Check if it's the RLS configuration error
      if (error instanceof Error && error.message.includes('app.current_organization_id')) {
        console.warn('RLS configuration issue detected. Service task templates feature is temporarily unavailable.');
        set({ 
          loading: false,
          error: 'I template dei task sono temporaneamente non disponibili a causa di un problema di configurazione del database.'
        });
        return null;
      }
      
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento del template',
        loading: false 
      });
      return null;
    }
  },

  deleteTemplate: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('service_task_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        templates: state.templates.filter(template => template.id !== id),
        selectedTemplate: state.selectedTemplate?.id === id ? null : state.selectedTemplate,
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error deleting service task template:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione del template',
        loading: false 
      });
      return false;
    }
  },

  duplicateTemplate: async (id: string, newServiceId?: string) => {
    try {
      set({ loading: true, error: null });

      // Get the original template
      const { data: originalTemplate, error: fetchError } = await supabase
        .from('service_task_templates')
        .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Create duplicate with new data
      const duplicateData = {
        ...originalTemplate,
        id: undefined,
        service_id: newServiceId || originalTemplate.service_id,
        task_title: `${originalTemplate.task_title} (Copia)`,
        created_by: undefined,
        updated_by: undefined,
        created_at: undefined,
        updated_at: undefined
      };

      const newTemplate = await get().createTemplate(duplicateData);
      
      if (newTemplate) {
        // Duplicate checklist items
        const { data: checklistItems } = await supabase
          .from('service_task_template_checklist')
          .select(`
          *,
          service:services(id, title),
          checklist_items:service_task_template_checklist(*)
        `)
          .eq('template_id', id);

        if (checklistItems) {
          for (const item of checklistItems) {
            await get().createChecklistItem(newTemplate.id, {
              title: item.title,
              description: item.description || '',
              is_required: item.is_required,
              sort_order: item.sort_order
            });
          }
        }
      }

      set({ loading: false });
      return newTemplate;
    } catch (error) {
      console.error('Error duplicating service task template:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nella duplicazione del template',
        loading: false 
      });
      return null;
    }
  },

  reorderTemplates: async (serviceId: string, templateIds: string[]) => {
    try {
      set({ loading: true, error: null });

      // Update task order for each template
      const updates = templateIds.map((id, index) => 
        supabase
          .from('service_task_templates')
          .update({ task_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);

      // Refresh templates
      await get().fetchTemplatesByService(serviceId);

      set({ loading: false });
      return true;
    } catch (error) {
      console.error('Error reordering templates:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel riordino dei template',
        loading: false 
      });
      return false;
    }
  },

  // Template Section Actions
  createSection: async (serviceId: string, sectionName: string) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Utente non autenticato');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('Organizzazione non trovata');

      // Generate section ID
      const sectionId = `section_${sectionName.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;

      // Create section as a template with is_header=true
      const sectionData = {
        organization_id: userProfile.organization_id,
        service_id: serviceId,
        name: sectionName,
        description: '',
        is_header: true,
        section: sectionId,
        trigger_event: 'service_added',
        trigger_days_offset: 0,
        assign_to_type: 'deal_referent',
        priority: 'medium',
        is_active: true,
        created_by: userData.user.id,
        updated_by: userData.user.id
      };

      const { data, error } = await supabase
        .from('service_task_templates')
        .insert(sectionData)
        .select('*')
        .single();

      if (error) throw error;

      // Refresh templates
      await get().fetchTemplatesByService(serviceId);

      set({ loading: false });
      return data;
    } catch (error) {
      console.error('Error creating section:', error);
      set({
        error: friendlyDbError(error) || 'Errore nella creazione della sezione',
        loading: false
      });
      return null;
    }
  },

  updateSection: async (sectionId: string, name: string) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Utente non autenticato');

      const { error } = await supabase
        .from('service_task_templates')
        .update({
          name: name,
          updated_by: userData.user.id
        })
        .eq('id', sectionId)
        .eq('is_header', true);

      if (error) throw error;

      // Update local state
      set(state => ({
        templates: state.templates.map(t =>
          t.id === sectionId ? { ...t, title: name, name: name } : t
        ),
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error updating section:', error);
      set({
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento della sezione',
        loading: false
      });
      return false;
    }
  },

  deleteSection: async (sectionId: string) => {
    try {
      set({ loading: true, error: null });

      // First, get the section to find its section ID
      const { data: sectionTemplate } = await supabase
        .from('service_task_templates')
        .select('section, service_id')
        .eq('id', sectionId)
        .eq('is_header', true)
        .single();

      if (!sectionTemplate) throw new Error('Sezione non trovata');

      // Move all templates in this section to no section (null)
      await supabase
        .from('service_task_templates')
        .update({ section: null })
        .eq('section', sectionTemplate.section)
        .eq('is_header', false);

      // Delete the section header
      const { error } = await supabase
        .from('service_task_templates')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;

      // Refresh templates
      if (sectionTemplate.service_id) {
        await get().fetchTemplatesByService(sectionTemplate.service_id);
      }

      set({ loading: false });
      return true;
    } catch (error) {
      console.error('Error deleting section:', error);
      set({
        error: friendlyDbError(error) || 'Errore nell\'eliminazione della sezione',
        loading: false
      });
      return false;
    }
  },

  moveTemplateToSection: async (templateId: string, sectionId: string | null) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Utente non autenticato');

      // If sectionId is provided, get the section's internal section ID
      let targetSection: string | undefined = undefined;
      if (sectionId) {
        const { data: sectionTemplate } = await supabase
          .from('service_task_templates')
          .select('section')
          .eq('id', sectionId)
          .eq('is_header', true)
          .single();

        if (sectionTemplate?.section) {
          targetSection = sectionTemplate.section;
        }
      }

      const { error } = await supabase
        .from('service_task_templates')
        .update({
          section: targetSection || null, // DB accepts null
          updated_by: userData.user.id
        })
        .eq('id', templateId);

      if (error) throw error;

      // Update local state - use null for consistency with DB
      set(state => ({
        templates: state.templates.map(t =>
          t.id === templateId ? { ...t, section: targetSection || null } : t
        ),
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error moving template to section:', error);
      set({
        error: friendlyDbError(error) || 'Errore nello spostamento del template',
        loading: false
      });
      return false;
    }
  },

  // JSON Export/Import
  exportTemplatesAsJson: async (serviceId: string) => {
    try {
      const { templates } = get();
      const serviceTemplates = templates.filter(t => t.service_id === serviceId);

      // Separate sections and tasks
      const sectionHeaders = serviceTemplates.filter(t => t.is_header);
      const tasks = serviceTemplates.filter(t => !t.is_header);

      // Build sections array
      const sections: Array<{
        name: string;
        order: number;
        tasks: Array<{
          title: string;
          description: string;
          priority: string;
          days_after_start: number;
          estimated_hours: number | null;
          assign_to_type: string;
          trigger_event: string;
          checklist: Array<{ title: string; is_required: boolean }>;
        }>;
      }> = [];

      // Group tasks by section
      const tasksBySection = new Map<string | undefined, typeof tasks>();
      tasks.forEach(task => {
        const sectionId = task.section;
        if (!tasksBySection.has(sectionId)) {
          tasksBySection.set(sectionId, []);
        }
        tasksBySection.get(sectionId)!.push(task);
      });

      // Build sections with their tasks
      sectionHeaders
        .sort((a, b) => (a.task_order || 0) - (b.task_order || 0))
        .forEach(header => {
          const sectionTasks = tasksBySection.get(header.section) || [];
          sectionTasks.sort((a, b) => (a.task_order || 0) - (b.task_order || 0));

          sections.push({
            name: header.title || 'Sezione',
            order: header.task_order || 0,
            tasks: sectionTasks.map(t => ({
              title: t.title || '',
              description: t.description || '',
              priority: t.priority || 'medium',
              days_after_start: t.delay_days || 0,
              estimated_hours: null, // Not directly available in type
              assign_to_type: t.assign_to_type || 'deal_owner',
              trigger_event: t.trigger_event || 'deal_signed',
              checklist: (t.checklist_items || []).map((c: { title: string; is_required?: boolean }) => ({
                title: c.title,
                is_required: c.is_required || false
              }))
            }))
          });
        });

      // Tasks without section
      const tasksWithoutSection = tasksBySection.get(undefined) || [];
      tasksWithoutSection.sort((a, b) => (a.task_order || 0) - (b.task_order || 0));

      // Fetch organization users for the schema
      const { data: userData } = await supabase.auth.getUser();
      let organizationUsers: Array<{ id: string; name: string; email: string }> = [];

      if (userData.user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (userProfile?.organization_id) {
          const { data: users } = await supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .eq('organization_id', userProfile.organization_id)
            .eq('active', true) // Escludi gli utenti disattivati
            .order('full_name');

          organizationUsers = (users || []).map(u => ({
            id: u.id,
            name: u.full_name || u.email || 'Utente',
            email: u.email || ''
          }));
        }
      }

      // Schema documentazione per AI
      const schema = {
        _description: "Schema che descrive tutte le opzioni disponibili per creare task template. Usa questo come riferimento per generare nuove task.",
        section: {
          name: "string (obbligatorio) - Nome della sezione, es: 'Fase 1 - Analisi', 'Setup Iniziale', 'Consegna'",
          order: "number - Ordine di visualizzazione (1, 2, 3...)"
        },
        task: {
          title: "string (obbligatorio) - Titolo della task, es: 'Analisi requisiti', 'Configurazione ambiente'",
          description: "string - Descrizione dettagliata di cosa fare",
          priority: {
            _type: "enum",
            values: ["low", "medium", "high"],
            _description: "Priorità: low=bassa, medium=media, high=alta"
          },
          days_after_start: "number - Giorni dopo l'evento trigger per la scadenza. 0=stesso giorno, 7=una settimana dopo",
          estimated_hours: "number - Ore stimate per completare la task. null se non specificato",
          assign_to_type: {
            _type: "enum",
            values: ["deal_referent", "specific_user", "partner", "auto"],
            _description: "Tipo di assegnazione automatica",
            options: {
              deal_referent: "Assegna al referente del deal (il commerciale che ha creato/gestisce il deal)",
              specific_user: "Assegna a un utente specifico - usa assigned_user_id con uno degli ID dalla lista 'organization_users'",
              partner: "Assegna a un partner esterno",
              auto: "Assegnazione automatica basata su regole"
            }
          },
          assigned_user_id: "string (UUID) - ID dell'utente specifico. Obbligatorio se assign_to_type='specific_user'. Vedi lista 'organization_users' per gli ID disponibili",
          trigger_event: {
            _type: "enum",
            values: ["service_added", "deal_signed", "payment_received", "custom_date"],
            _description: "Evento che attiva la creazione della task",
            options: {
              service_added: "Quando il servizio viene aggiunto al deal",
              deal_signed: "Quando il deal viene firmato/accettato dal cliente",
              payment_received: "Quando viene ricevuto un pagamento",
              custom_date: "In una data personalizzata"
            }
          },
          task_location: {
            _type: "enum",
            values: ["deal", "project"],
            _description: "IMPORTANTE: Dove creare la task",
            options: {
              deal: "Task per il COMMERCIALE - Appare nel deal, gestita dal team vendite (es: follow-up cliente, invio preventivo, sollecito pagamento)",
              project: "Task per il TEAM OPERATIVO - Appare nel progetto, gestita dal team che esegue il lavoro (es: sviluppo, consegna, supporto tecnico)"
            }
          },
          checklist: {
            _type: "array",
            _description: "Lista di sotto-elementi/step da completare per questa task",
            item: {
              title: "string (obbligatorio) - Testo dell'elemento checklist",
              is_required: "boolean - Se true, l'elemento deve essere completato prima di poter chiudere la task"
            }
          }
        },
        organization_users: organizationUsers.length > 0 ? organizationUsers : "Nessun utente trovato - effettua login per vedere la lista"
      };

      // Istruzioni per l'AI
      const aiInstructions = {
        _titolo: "ISTRUZIONI PER L'AI - Leggi prima di generare task",
        introduzione: "Questo JSON descrive la struttura delle task template per un servizio. Quando l'utente ti chiede di generare task, segui queste istruzioni.",
        domande_da_fare: [
          {
            tema: "Assegnazioni",
            domanda: "Chiedi: 'Chi deve eseguire ogni task? Distingui tra task per il commerciale (deal) e task per il team operativo (project). Per task specifiche, dimmi se vanno assegnate a un utente particolare.'",
            note: "Usa task_location='deal' per task commerciali (follow-up, preventivi, pagamenti) e task_location='project' per task operative (sviluppo, consegna, supporto)"
          },
          {
            tema: "Tempistiche",
            domanda: "Chiedi: 'Quali sono le tempistiche? Per ogni fase/task, quanti giorni dopo la firma del contratto deve essere completata?'",
            note: "Usa days_after_start per impostare le scadenze relative. Es: 0=subito, 7=una settimana, 30=un mese"
          },
          {
            tema: "Priorità",
            domanda: "Chiedi: 'Quali task sono urgenti/critiche e quali possono attendere?'",
            note: "Usa priority: 'high' per task critiche, 'medium' per normali, 'low' per meno urgenti"
          },
          {
            tema: "Checklist",
            domanda: "Chiedi: 'Ci sono task che richiedono più passaggi o verifiche intermedie? Se sì, quali sono gli step?'",
            note: "Usa checklist[] per creare sotto-elementi. Imposta is_required=true per step obbligatori"
          },
          {
            tema: "Trigger",
            domanda: "Chiedi: 'Quando devono partire le task? Alla firma del contratto, all'aggiunta del servizio, o al ricevimento del pagamento?'",
            note: "Usa trigger_event per definire quando creare le task: deal_signed (più comune), service_added, payment_received"
          }
        ],
        esempio_conversazione: "Utente: 'Genera task per un servizio di sviluppo web'\nAI: 'Per creare le task ottimali, ho bisogno di alcune informazioni:\n1. Chi gestirà le task commerciali (follow-up, fatturazione) e chi quelle operative (sviluppo, testing)?\n2. Quali sono le tempistiche tipiche? Es: analisi requisiti entro 3 giorni, sviluppo entro 2 settimane, testing entro 1 settimana dalla consegna?\n3. Ci sono utenti specifici a cui assegnare determinate task? (vedi lista organization_users)\n4. Quali task sono critiche e quali possono essere posticipate?'"
      };

      const exportData = {
        version: '1.0',
        service_id: serviceId,
        exported_at: new Date().toISOString(),
        _istruzioni_per_ai: aiInstructions,
        schema,
        sections,
        tasks_without_section: tasksWithoutSection.map(t => ({
          title: t.title || '',
          description: t.description || '',
          priority: t.priority || 'medium',
          days_after_start: t.delay_days || 0,
          estimated_hours: null,
          assign_to_type: t.assign_to_type || 'deal_owner',
          trigger_event: t.trigger_event || 'deal_signed',
          checklist: (t.checklist_items || []).map((c: { title: string; is_required?: boolean }) => ({
            title: c.title,
            is_required: c.is_required || false
          }))
        }))
      };

      // If no templates, return example JSON with schema
      if (serviceTemplates.length === 0) {
        return JSON.stringify({
          version: '1.0',
          service_id: null,
          example: true,
          _nota: "Questo è un esempio. Modifica 'sections' e 'tasks_without_section' con le tue task.",
          _istruzioni_per_ai: aiInstructions,
          schema,
          sections: [
            {
              name: 'Esempio Sezione',
              order: 1,
              tasks: [
                {
                  title: 'Esempio Task',
                  description: 'Descrizione della task',
                  priority: 'medium',
                  days_after_start: 0,
                  estimated_hours: 1,
                  assign_to_type: 'deal_referent',
                  trigger_event: 'deal_signed',
                  task_location: 'project',
                  checklist: [
                    { title: 'Elemento checklist 1', is_required: true },
                    { title: 'Elemento checklist 2', is_required: false }
                  ]
                }
              ]
            }
          ],
          tasks_without_section: []
        }, null, 2);
      }

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Error exporting templates:', error);
      return JSON.stringify({ error: 'Errore nell\'esportazione' });
    }
  },

  importTemplatesFromJson: async (serviceId: string, json: string, mode: 'replace' | 'append', onProgress?: (current: number, total: number) => void) => {
    try {
      set({ loading: true, error: null });

      // Parse JSON
      let data;
      try {
        data = JSON.parse(json);
      } catch {
        set({ loading: false });
        return { success: false, imported: 0, error: 'JSON non valido' };
      }

      // Validate structure
      if (!data.version) {
        set({ loading: false });
        return { success: false, imported: 0, error: 'Formato JSON non valido: manca il campo version' };
      }

      // Calculate total items to import
      let totalItems = 0;
      const sections = data.sections || [];
      sections.forEach((section: { tasks?: unknown[] }) => {
        totalItems++; // section header
        totalItems += section.tasks?.length || 0;
      });
      totalItems += (data.tasks_without_section || []).length;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        set({ loading: false });
        return { success: false, imported: 0, error: 'Utente non autenticato' };
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        set({ loading: false });
        return { success: false, imported: 0, error: 'Organizzazione non trovata' };
      }

      // If replace mode, delete existing templates
      if (mode === 'replace') {
        const { error: deleteError } = await supabase
          .from('service_task_templates')
          .delete()
          .eq('service_id', serviceId);

        if (deleteError) {
          console.error('Error deleting existing templates:', deleteError);
          set({ loading: false });
          return { success: false, imported: 0, error: 'Errore nell\'eliminazione dei template esistenti' };
        }
      }

      let importedCount = 0;

      // Report initial progress
      if (onProgress) onProgress(0, totalItems);

      // Import sections and their tasks
      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const section = sections[sectionIndex];
        // Create section header
        const sectionId = `section_${(section.name || 'sezione').toLowerCase().replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

        const { error: sectionError } = await supabase
          .from('service_task_templates')
          .insert({
            organization_id: userProfile.organization_id,
            service_id: serviceId,
            name: section.name || 'Sezione',
            description: '',
            is_header: true,
            section: sectionId,
            trigger_event: 'service_added',
            trigger_days_offset: 0,
            assign_to_type: 'deal_referent',
            priority: 'medium',
            is_active: true,
            task_order: section.order || sectionIndex,
            created_by: userData.user.id,
            updated_by: userData.user.id
          });

        if (sectionError) {
          console.error('Error creating section:', sectionError);
          continue;
        }

        importedCount++;
        if (onProgress) onProgress(importedCount, totalItems);

        // Create tasks in this section
        const sectionTasks = section.tasks || [];
        for (let taskIndex = 0; taskIndex < sectionTasks.length; taskIndex++) {
          const task = sectionTasks[taskIndex];
          const { data: newTemplate, error: taskError } = await supabase
            .from('service_task_templates')
            .insert({
              organization_id: userProfile.organization_id,
              service_id: serviceId,
              name: task.title || 'Task',
              description: task.description || '',
              is_header: false,
              section: sectionId,
              trigger_event: task.trigger_event || 'deal_signed',
              trigger_days_offset: task.days_after_start || 0,
              assign_to_type: task.assign_to_type === 'deal_owner' ? 'deal_referent' : (task.assign_to_type || 'deal_referent'),
              assigned_user_id: task.assigned_user_id || null,
              assigned_partner_id: task.assigned_partner_id || null,
              priority: task.priority || 'medium',
              estimated_duration_minutes: task.estimated_hours ? task.estimated_hours * 60 : null,
              task_location: task.task_location || 'project',
              is_active: true,
              task_order: (sectionIndex * 100) + taskIndex, // Ordine: sezione * 100 + indice task
              created_by: userData.user.id,
              updated_by: userData.user.id
            })
            .select()
            .single();

          if (taskError) {
            console.error('Error creating task:', taskError);
            continue;
          }

          importedCount++;
          if (onProgress) onProgress(importedCount, totalItems);

          // Create checklist items
          if (task.checklist && task.checklist.length > 0 && newTemplate) {
            for (let i = 0; i < task.checklist.length; i++) {
              const checklistItem = task.checklist[i];
              await supabase
                .from('service_task_template_checklist')
                .insert({
                  template_id: newTemplate.id,
                  title: checklistItem.title,
                  description: '',
                  is_required: checklistItem.is_required || false,
                  sort_order: i
                });
            }
          }
        }
      }

      // Import tasks without section
      const tasksWithoutSection = data.tasks_without_section || [];
      const baseSectionOrder = sections.length * 100; // Offset per task senza sezione
      for (let taskIndex = 0; taskIndex < tasksWithoutSection.length; taskIndex++) {
        const task = tasksWithoutSection[taskIndex];
        const { data: newTemplate, error: taskError } = await supabase
          .from('service_task_templates')
          .insert({
            organization_id: userProfile.organization_id,
            service_id: serviceId,
            name: task.title || 'Task',
            description: task.description || '',
            is_header: false,
            section: null,
            trigger_event: task.trigger_event || 'deal_signed',
            trigger_days_offset: task.days_after_start || 0,
            assign_to_type: task.assign_to_type === 'deal_owner' ? 'deal_referent' : (task.assign_to_type || 'deal_referent'),
            assigned_user_id: task.assigned_user_id || null,
            assigned_partner_id: task.assigned_partner_id || null,
            priority: task.priority || 'medium',
            estimated_duration_minutes: task.estimated_hours ? task.estimated_hours * 60 : null,
            task_location: task.task_location || 'project',
            is_active: true,
            task_order: baseSectionOrder + taskIndex,
            created_by: userData.user.id,
            updated_by: userData.user.id
          })
          .select()
          .single();

        if (taskError) {
          console.error('Error creating task:', taskError);
          continue;
        }

        importedCount++;
        if (onProgress) onProgress(importedCount, totalItems);

        // Create checklist items
        if (task.checklist && task.checklist.length > 0 && newTemplate) {
          for (let i = 0; i < task.checklist.length; i++) {
            const checklistItem = task.checklist[i];
            await supabase
              .from('service_task_template_checklist')
              .insert({
                template_id: newTemplate.id,
                title: checklistItem.title,
                description: '',
                is_required: checklistItem.is_required || false,
                sort_order: i
              });
          }
        }
      }

      // Refresh templates
      await get().fetchTemplatesByService(serviceId);

      set({ loading: false });
      return { success: true, imported: importedCount };
    } catch (error) {
      console.error('Error importing templates:', error);
      set({
        error: friendlyDbError(error) || 'Errore nell\'importazione',
        loading: false
      });
      return { success: false, imported: 0, error: friendlyDbError(error) || 'Errore sconosciuto' };
    }
  },

  // Template Checklist Actions
  fetchTemplateChecklist: async (templateId: string) => {
    try {
      const { data, error } = await supabase
        .from('service_task_template_checklist')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching template checklist:', error);
      return [];
    }
  },

  createChecklistItem: async (templateId: string, data: Partial<ServiceTaskTemplateChecklistFormData>) => {
    try {
      const itemData = {
        template_id: templateId,
        title: data.title,
        description: data.description,
        is_required: data.is_required || false,
        sort_order: data.sort_order || 0
      };

      const { data: newItem, error } = await supabase
        .from('service_task_template_checklist')
        .insert(itemData)
        .select()
        .single();

      if (error) throw error;

      return newItem;
    } catch (error) {
      console.error('Error creating checklist item:', error);
      return null;
    }
  },

  updateChecklistItem: async (id: string, data: Partial<ServiceTaskTemplateChecklistFormData>) => {
    try {
      const { data: updatedItem, error } = await supabase
        .from('service_task_template_checklist')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return updatedItem;
    } catch (error) {
      console.error('Error updating checklist item:', error);
      return null;
    }
  },

  deleteChecklistItem: async (id: string) => {
    try {
      const { error } = await supabase
        .from('service_task_template_checklist')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      return false;
    }
  },

  reorderChecklistItems: async (templateId: string, itemIds: string[]) => {
    try {
      const updates = itemIds.map((id, index) => 
        supabase
          .from('service_task_template_checklist')
          .update({ sort_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);
      return true;
    } catch (error) {
      console.error('Error reordering checklist items:', error);
      return false;
    }
  },

  // Task Instances Actions
  fetchTaskInstances: async () => {
    try {
      set({ loading: true, error: null });
      
      const { instancesFilter } = get();
      let query = supabase
        .from('service_task_instances')
        .select(`
          *,
          template:service_task_templates(id, title),
          deal:deals(id, title),
          deal_service:deal_services(id),
          assigned_user:user_profiles(id, full_name, email),
          checklist_items:service_task_instance_checklist(*)
        `)
        .order('due_date', { ascending: true });

      // Apply filters
      if (instancesFilter.search) {
        query = query.or(`title.ilike.%${instancesFilter.search}%,description.ilike.%${instancesFilter.search}%`);
      }
      
      if (instancesFilter.deal_id) {
        query = query.eq('deal_id', instancesFilter.deal_id);
      }
      
      if (instancesFilter.assigned_to) {
        query = query.eq('assigned_to', instancesFilter.assigned_to);
      }
      
      if (instancesFilter.status) {
        query = query.eq('status', instancesFilter.status);
      }
      
      if (instancesFilter.task_type) {
        query = query.eq('task_type', instancesFilter.task_type);
      }
      
      if (instancesFilter.priority) {
        query = query.eq('priority', instancesFilter.priority);
      }
      
      if (instancesFilter.due_date_from) {
        query = query.gte('due_date', instancesFilter.due_date_from);
      }
      
      if (instancesFilter.due_date_to) {
        query = query.lte('due_date', instancesFilter.due_date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ templateInstances: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching task instances:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento delle istanze',
        loading: false 
      });
    }
  },

  fetchTaskInstancesByDeal: async (dealId: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_task_instances')
        .select(`
          *,
          template:service_task_templates(id, title),
          deal:deals(id, title),
          deal_service:deal_services(id),
          assigned_user:user_profiles(id, full_name, email),
          checklist_items:service_task_instance_checklist(*)
        `)
        .eq('deal_id', dealId)
        .order('due_date', { ascending: true });

      if (error) throw error;

      set({ templateInstances: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching deal task instances:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei task del deal',
        loading: false 
      });
    }
  },

  fetchTaskInstanceById: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('service_task_instances')
        .select(`
          *,
          template:service_task_templates(id, title),
          deal:deals(id, title),
          deal_service:deal_services(id),
          assigned_user:user_profiles(id, full_name, email),
          checklist_items:service_task_instance_checklist(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      set({ selectedInstance: data, loading: false });
    } catch (error) {
      console.error('Error fetching task instance:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dell\'istanza',
        loading: false 
      });
    }
  },

  createTaskInstance: async (data: Partial<ServiceTaskInstanceFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // Clean up UUID fields - convert empty strings to null
      const cleanData = { ...data };
      
      // UUID fields that should be null if empty
      const uuidFields = ['template_id', 'deal_id', 'deal_service_id', 'assigned_to'];
      uuidFields.forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === undefined) {
          cleanData[field] = null;
        }
      });

      const instanceData = {
        ...cleanData,
        organization_id: userProfile.organization_id
      };

      const { data: newInstance, error } = await supabase
        .from('service_task_instances')
        .insert(instanceData)
        .select(`
          *,
          template:service_task_templates(id, title),
          deal:deals(id, title),
          deal_service:deal_services(id),
          assigned_user:user_profiles(id, full_name, email),
          checklist_items:service_task_instance_checklist(*)
        `)
        .single();

      if (error) throw error;

      set(state => ({
        templateInstances: [...state.templateInstances, newInstance],
        loading: false
      }));

      return newInstance;
    } catch (error) {
      console.error('Error creating task instance:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nella creazione dell\'istanza',
        loading: false 
      });
      return null;
    }
  },

  updateTaskInstance: async (id: string, data: Partial<ServiceTaskInstanceFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Clean up UUID fields - convert empty strings to null
      const cleanData = { ...data };
      
      // UUID fields that should be null if empty
      const uuidFields = ['template_id', 'deal_id', 'deal_service_id', 'assigned_to'];
      uuidFields.forEach(field => {
        if (cleanData[field] === '' || cleanData[field] === undefined) {
          cleanData[field] = null;
        }
      });

      const updateData = {
        ...cleanData
      };

      const { data: updatedInstance, error } = await supabase
        .from('service_task_instances')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          template:service_task_templates(id, title),
          deal:deals(id, title),
          deal_service:deal_services(id),
          assigned_user:user_profiles(id, full_name, email),
          checklist_items:service_task_instance_checklist(*)
        `)
        .single();

      if (error) throw error;

      set(state => ({
        templateInstances: state.templateInstances.map(instance => 
          instance.id === id ? updatedInstance : instance
        ),
        selectedInstance: state.selectedInstance?.id === id ? updatedInstance : state.selectedInstance,
        loading: false
      }));

      return updatedInstance;
    } catch (error) {
      console.error('Error updating task instance:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento dell\'istanza',
        loading: false 
      });
      return null;
    }
  },

  deleteTaskInstance: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('service_task_instances')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        templateInstances: state.templateInstances.filter(instance => instance.id !== id),
        selectedInstance: state.selectedInstance?.id === id ? null : state.selectedInstance,
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error deleting task instance:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione dell\'istanza',
        loading: false 
      });
      return false;
    }
  },

  updateInstanceChecklistItem: async (id: string, isCompleted: boolean, timeSpentMinutes?: number) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const updateData: any = {
        is_completed: isCompleted
      };

      if (isCompleted) {
        updateData.completed_at = new Date().toISOString();
      } else {
        updateData.completed_at = null;
      }

      if (timeSpentMinutes !== undefined) {
        updateData.time_spent_minutes = timeSpentMinutes;
      }

      const { error } = await supabase
        .from('service_task_instance_checklist')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error updating checklist item:', error);
      return false;
    }
  },

  // Automation
  createTasksFromTemplates: async (dealId: string, dealServiceId: string, serviceId: string, triggerEvent = 'service_added') => {
    try {
      console.log('🔧 Creating tasks from templates:', {
        dealId,
        dealServiceId, 
        serviceId,
        triggerEvent
      });

      // Get user data for organization_id and created_by
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!userProfile?.organization_id) throw new Error('User organization not found');

      // Call RPC with correct parameters matching the database function
      const { data, error } = await supabase.rpc('create_tasks_from_service_templates', {
        p_service_id: serviceId,
        p_trigger_event: triggerEvent,
        p_organization_id: userProfile.organization_id,
        p_created_by: userData.user.id,
        p_deal_id: dealId
      });

      if (error) {
        console.error('❌ RPC Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          fullError: error
        });
        throw error;
      }

      const result = data || 0;
      console.log(`🔧 Task creation result: ${result} tasks created`);
      return result;
    } catch (error) {
      console.error('❌ Error creating tasks from templates:', error);
      return 0;
    }
  },

  // Utilities
  setTemplatesFilter: (filter: ServiceTaskTemplatesFilter) => {
    set({ templatesFilter: filter });
  },

  setInstancesFilter: (filter: ServiceTaskInstancesFilter) => {
    set({ instancesFilter: filter });
  },

  clearFilters: () => {
    set({ templatesFilter: {}, instancesFilter: {} });
  },

  setSelectedTemplate: (template: ServiceTaskTemplate | null) => {
    set({ selectedTemplate: template });
  },

  setSelectedInstance: (instance: ServiceTaskInstance | null) => {
    set({ selectedInstance: instance });
  },

  clearError: () => {
    set({ error: null });
  }
}));